/**
 * Home: 2nd Content row boxes
 */
import React, { useContext, useState, useEffect } from "react";
import { Box, Grid, Typography, Stack } from "@mui/material";
import { AppContext } from "../context";
import { pathJoinUrl } from "@smfa/utils/src";
import joinimg from "../assets/img/join-smfa_icon.png";

// Common components
import LimitedTextField from "../components/common/LimitedTextField";
import FileUploadArea from "../components/common/FileUploadArea";
import FormActionBar from "../components/common/FormActionBar";

// API actions
import { addeditJoin, getJoinlist } from "../actions/home.actions";
import ToolBody from "../components/common/ToolBody";

const Joinsmfa = () => {
  const { storage } = useContext(AppContext);

  // Form state
  const addHomeJoin_default = {
    home_join_section_id: 0,
    image: "",
    image_name: "",
    image_error: false,
    image_mob: "",
    image_mob_name: "",
    image_mob_error: false,
    title: "",
    title_maxlenth: 20,
    title_error: false,
    short_description: "",
    short_description_maxlenth: 300,
    short_description_error: false,
    title_sc: "",
    title_sc_maxlenth: 20,
    title_sc_error: false,
    short_description_sc: "",
    short_description_sc_maxlenth: 300,
    short_description_sc_error: false,
  };

  // Component state
  const [addHomeJoin, setHomeJoin] = useState(addHomeJoin_default);
  const [deskimage, setDeskImage] = useState(joinimg);
  const [mobimage, setMobImage] = useState("");
  const [loader, setLoader] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // File upload handlers
  const onImageChange = (event) => {
    setSuccessMsg("");
    setErrorMsg("");

    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setDeskImage(URL.createObjectURL(file));
      setHomeJoin((prev) => ({
        ...prev,
        image: file,
        image_name: file.name,
        image_error: false,
      }));
    }
  };

  const onMobImageChange = (event) => {
    setSuccessMsg("");
    setErrorMsg("");

    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setMobImage(URL.createObjectURL(file));
      setHomeJoin((prev) => ({
        ...prev,
        image_mob: file,
        image_mob_name: file.name,
        image_mob_error: false,
      }));
    }
  };

  const clearUpload = (field, clearPreview) => {
    setHomeJoin((prev) => ({
      ...prev,
      [field]: "",
      [`${field}_name`]: "",
    }));
    clearPreview("");
  };

  // Text field change handler
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSuccessMsg("");
    setErrorMsg("");

    setHomeJoin((prev) => {
      const maxLength = prev[`${name}_maxlenth`];
      // Only update if within character limit
      if (!maxLength || value.length <= maxLength) {
        return {
          ...prev,
          [name]: value,
          [`${name}_error`]: false,
        };
      }
      return prev;
    });
  };

  // Form submission
  const submitForm = () => {
    setLoader(true);
    setSuccessMsg("");
    setErrorMsg("");

    // Form validation
    let hasError = false;
    const updatedState = { ...addHomeJoin };

    // Check required fields
    const requiredFields = [
      "title",
      "short_description",
      "title_sc",
      "short_description_sc",
    ];

    requiredFields.forEach((field) => {
      if (!updatedState[field]) {
        updatedState[`${field}_error`] = true;
        hasError = true;
      }
    });

    if (hasError) {
      setHomeJoin(updatedState);
      setLoader(false);
      return;
    }

    // Prepare form data
    const formData = new FormData();
    formData.append("home_join_section_id", updatedState.home_join_section_id);
    formData.append("image", updatedState.image);
    formData.append("image_mob", updatedState.image_mob);
    formData.append("title", updatedState.title);
    formData.append("short_description", updatedState.short_description);
    formData.append("title_sc", updatedState.title_sc);
    formData.append("short_description_sc", updatedState.short_description_sc);

    // Submit to API
    const isNew = updatedState.home_join_section_id === 0;
    addeditJoin(formData)
      .then((res) => {
        if (res.data.status === true) {
          setSuccessMsg(
            isNew
              ? "Join Section Added Successfully!"
              : "Join Section Updated Successfully!",
          );

          // Hide success message after delay
          setTimeout(() => {
            setSuccessMsg("");
          }, 3000);
        } else {
          setErrorMsg(
            res.data.message || "Something went wrong, please try again!",
          );
        }
      })
      .catch(() => {
        setErrorMsg("Something went wrong, please try again!");
      })
      .finally(() => {
        setLoader(false);
      });
  };

  // Reset form
  const resetForm = () => {
    setHomeJoin(addHomeJoin_default);
    setDeskImage(joinimg);
    setMobImage("");
    setErrorMsg("");
    setSuccessMsg("");
  };

  // Load initial data
  useEffect(() => {
    getJoinlist().then((res) => {
      if (res.data.status === true) {
        const data = res.data.data;
        setHomeJoin({
          ...addHomeJoin_default,
          home_join_section_id: data.home_join_section_id || 0,
          title: data.title || "",
          title_sc: data.title_sc || "",
          short_description: data.short_description || "",
          short_description_sc: data.short_description_sc || "",
          image_name: data.image || "",
          image_mob_name: data.image_mob || "",
        });

        if (data.image) {
          setDeskImage(pathJoinUrl(storage[0].imageEndUrl, data.image));
        }
        if (data.image_mob) {
          setMobImage(pathJoinUrl(storage[0].imageEndUrl, data.image_mob));
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ToolBody title="Home: 2nd Content Row Boxes">
      <Grid container spacing={3}>
        {/* Left column */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack spacing={2}>
            <Typography variant="h6">Home: Left Side</Typography>

            <FileUploadArea
              title="Upload Left Image"
              description={
                <>
                  Best image Resolution: 70px X 70px
                  <br />
                  Supported files types: JPG, JPEG & PNG
                </>
              }
              previewUrl={deskimage}
              fileName={addHomeJoin.image_name}
              onChange={onImageChange}
              deleteRequest={{
                resource: "home-join",
                id: addHomeJoin.home_join_section_id,
                field: "image",
              }}
              onRemove={() => clearUpload("image", setDeskImage)}
              accept=".png,.jpg,.jpeg"
              error={addHomeJoin.image_error}
              errorText="Upload an image"
            />

            <LimitedTextField
              name="title"
              label="Title"
              value={addHomeJoin.title}
              onChange={handleInputChange}
              maxLength={addHomeJoin.title_maxlenth}
              error={addHomeJoin.title_error}
              errorText="Enter a title"
            />

            <LimitedTextField
              name="short_description"
              label="Short Description"
              placeholder="Short Description (Left)"
              multiline
              rows={3}
              value={addHomeJoin.short_description}
              onChange={handleInputChange}
              maxLength={addHomeJoin.short_description_maxlenth}
              error={addHomeJoin.short_description_error}
              errorText="Enter a short description"
            />
          </Stack>
        </Grid>

        {/* Right column */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack spacing={2}>
            <Typography variant="h6">Home: Right Side</Typography>

            <FileUploadArea
              title="Upload Right Image"
              description={
                <>
                  Best image Resolution: 70px X 70px
                  <br />
                  Supported files types: JPG, JPEG & PNG
                </>
              }
              previewUrl={mobimage}
              fileName={addHomeJoin.image_mob_name}
              onChange={onMobImageChange}
              deleteRequest={{
                resource: "home-join",
                id: addHomeJoin.home_join_section_id,
                field: "image_mob",
              }}
              onRemove={() => clearUpload("image_mob", setMobImage)}
              accept=".png,.jpg,.jpeg"
              error={addHomeJoin.image_mob_error}
              errorText="Upload an image"
            />

            <LimitedTextField
              name="title_sc"
              label="Title (Right)"
              value={addHomeJoin.title_sc}
              onChange={handleInputChange}
              maxLength={addHomeJoin.title_sc_maxlenth}
              error={addHomeJoin.title_sc_error}
              errorText="Enter a title"
            />

            <LimitedTextField
              name="short_description_sc"
              label="Short Description (Right)"
              placeholder="Short Description"
              multiline
              rows={3}
              value={addHomeJoin.short_description_sc}
              onChange={handleInputChange}
              maxLength={addHomeJoin.short_description_sc_maxlenth}
              error={addHomeJoin.short_description_sc_error}
              errorText="Enter a short description"
            />
          </Stack>
        </Grid>
      </Grid>

      {/* Submit button and messages */}
      <Box sx={{ mt: 4 }}>
        <FormActionBar
          onSubmit={submitForm}
          onReset={resetForm}
          isSubmitting={loader}
          isEdit={addHomeJoin.home_join_section_id !== 0}
          successMessage={successMsg}
          errorMessage={errorMsg}
          submitLabel="Update"
        />
      </Box>
    </ToolBody>
  );
};

export default Joinsmfa;
